import qs from 'qs'
import token from './TokenStorage'

export const ERROR_401 = 'app/utils/ERROR_401'

export const ERROR_UNAUTHORIZED = 'unauthorized'
export const ERROR_SERVER_ERROR = 'server-error'
export const ERROR_NOT_FOUND = 'not-found'
export const ERROR_BAD_REQUEST = 'bad-request'

// Finazilla.Core.ErrorCodes.ReportDataStale. The report cell we drilled into points at
// RealBalance rows that no longer exist: recalculating actuals deletes and re-inserts them
// with new ids, so a report rendered before the recalculation cannot be drilled into until
// it has been fetched again.
export const ERROR_CODE_REPORT_DATA_STALE = 'E-0108'

// Finazilla.Core.ErrorCodes.ActualsCalculationInProgress. Report data could not be read because an
// actuals calculation is rewriting the figures of one of the report's companies, a month at a time,
// and half of a rewrite is not worth showing. A wait, not a failure: the request is repeated.
export const ERROR_CODE_ACTUALS_CALCULATION_IN_PROGRESS = 'E-0109'

// The API serializes known exceptions with PascalCase members, but accept both casings so
// this keeps working if the error response ever goes through the camelCase resolver.
export const isReportDataStaleError = (error) =>
  (error?.data?.Code ?? error?.data?.code) === ERROR_CODE_REPORT_DATA_STALE

// Both casings really do occur for the same error: ApiErrorResponse serializes itself through the
// camelCase resolver, while the exception filter that catches a mid-stream throw uses the default
// one. Which of the two answers a given request depends on where the throw came from.
export const errorCodeOf = (error) => error?.Code ?? error?.code

/**
 * The shape the error path expects. Every other call reaches handleError through catchError with
 * an axios response - `{ status, data }` - and ErrorComponent reads the server's message and code
 * out of `data`. A server-sent-event error arrives as the parsed body on its own, so handing it
 * over unwrapped loses both and renders a bare "Something went wrong".
 *
 * The status lives in the body here rather than on the response: a mid-stream throw is written
 * after the headers are already out, so the transport says 200 whatever went wrong.
 */
export const asApiError = (frame) => ({
  status: frame?.Status ?? frame?.status,
  data: frame,
})

// Structured detail belonging to the error code, from ApiErrorResponse.Details.
const detailsOf = (error) => error?.Details ?? error?.details

export const companyNamesOf = (error) => {
  const details = detailsOf(error)
  return details?.CompanyNames ?? details?.companyNames ?? []
}

export const apiConfig = {
  backendUrl: process.env.REACT_APP_BACKEND_API,
  gtUrl: process.env.REACT_APP_GT_API,
}

export const statusCodeToError = (statusCode) => {
  switch (statusCode) {
    case 400:
      return ERROR_BAD_REQUEST
    case 401:
    case 403:
      return ERROR_UNAUTHORIZED
    case 404:
      return ERROR_NOT_FOUND
    case 500:
      return ERROR_SERVER_ERROR
    default:
      return ERROR_SERVER_ERROR
  }
}

export const handleError = (error, failedAction, additionalData = {}) => {
  const errorType = statusCodeToError(error?.status || error?.data?.status)
  const { error401, otherError } = parseError(error)
  if (error401) {
    return {
      type: ERROR_401,
    }
  }
  const returnValue = failedAction({
    ...otherError,
    errorType,
    ...additionalData,
  })
  return returnValue
}

export const catchError = (error) => {
  if (error.response) {
    return Promise.reject(error.response)
  }
  return Promise.reject(error)
}

export const config = (override, apiUrl = apiConfig.backendUrl) => {
  try {
    if (apiUrl) {
      const { access_token } = token.getTokens()
      return Object.assign(
        {},
        {
          baseURL: apiUrl,
          headers: {
            Authorization: `Bearer ${access_token}`,
            'x-finazilla.request.client.user.guid': token.getProfileId(),
          },
        },
        override
      )
    } else {
      throw new Error(
        'API URL undefined. Add REACT_APP_BACKEND_API environment variable and run build again.'
      )
    }
  } catch (error) {
    console.error(error)
  }
}

export const paramsSerializer = (params) =>
  qs.stringify(params, { arrayFormat: 'repeat' })

export const parseError = (err) => {
  if (err.status === 401) return { error401: true }
  return err instanceof Error
    ? {
        otherError: {
          jsError: err.toString(),
        },
      }
    : {
        otherError: {
          ...err,
        },
      }
}

export const returnData = (axiosResponse) =>
  axiosResponse.data.data !== undefined
    ? axiosResponse.data.data
    : axiosResponse.data

export const NUM_ONLY_REGEX = /^([0-9]+|Infinity)$/
export const NUM_AND_CHAR_REGEX = /^[0-9A-Za-z._&]{1,9999999999}$/
export const accountNumberRange = {
  MAX: 999999,
  MIN: 1000,
}
